<template>
    <RedesignBaseSideModal
        :visible="visible"
        position="left"
        @close="closeModal"
    >
        <template #header>
            <div class="text-main-lg font-medium">
                {{ t('settings.settings') }}
            </div>
        </template>

        <div
            v-if="localConfig"
            class="flex flex-col gap-4"
        >
            <div>
                <div class="text-sm mb-1">
                    {{ t('common.customLabel') }}
                </div>
                <VcInput
                    v-model="localConfig.customLabel"
                    :placeholder="String(t('common.customLabel'))"
                />
            </div>

            <!-- CARD COLOR -->
            <div>
                <div class="text-sm mb-1">
                    {{ t('settings.customColor') }}
                </div>
                <RedesignWidgetColor
                    v-model:value="localConfig.customColor"
                    size="small"
                />
            </div>
            <div>
                <div class="text-sm mb-1">
                    {{ t('settings.thresholdDirection') }}
                </div>
                <div class="flex items-center gap-2">
                    <VcSwitch
                        v-model="localConfig.notificationConfiguration.reverseThreshold"
                    />
                    <span class="text-sm">
                        {{ localConfig.notificationConfiguration.reverseThreshold
                            ? t('widget.config.higherIsBetter')
                            : t('widget.config.lowerIsBetter')
                        }}
                    </span>
                </div>
            </div>
            <div>
                <div class="text-sm mb-1">
                    {{ t('widget.config.maximumWarningRangeValue') + getLabelSuffix() }}
                </div>
                <VcInputNumber
                    v-model="localConfig.notificationConfiguration.warningThreshold"
                    :min-value="0"
                    :step="1"
                />
            </div>
            <div>
                <div class="text-sm mb-1">
                    {{ t('settings.warningColor') }}
                </div>
                <RedesignWidgetColor
                    v-model:value="localConfig.notificationConfiguration.warningColor"
                    size="small"
                />
            </div>
            <div>
                <div class="text-sm mb-1">
                    {{ t('widget.config.maximumLimitRangeValue') + getLabelSuffix() }}
                </div>
                <VcInputNumber
                    v-model="localConfig.notificationConfiguration.limitThreshold"
                    :min-value="0"
                    :step="1"
                />
            </div>
            <div>
                <div class="text-sm mb-1">
                    {{ t('settings.limitColor') }}
                </div>
                <RedesignWidgetColor
                    v-model:value="localConfig.notificationConfiguration.limitColor"
                    size="small"
                />
            </div>

            <div>
                <div class="text-sm mb-1">
                    {{ t('settings.warningSound') }}
                </div>
                <SoundSelect
                    v-model="localConfig.notificationConfiguration.warningSound"
                    :options="warningSoundList"
                    class="w-full"
                />
            </div>
            <div>
                <div class="text-sm mb-1">
                    {{ t('settings.limitSound') }}
                </div>
                <SoundSelect
                    v-model="localConfig.notificationConfiguration.limitSound"
                    :options="warningSoundList"
                    class="w-full"
                />
            </div>
        </div>
        <template #footer>
            <VcButton
                type="outline"
                color="secondary"
                @click="closeModal"
            >
                {{ t('common.cancel') }}
            </VcButton>

            <VcButton
                type="default"
                color="primary"
                @click="onSave"
            >
                {{ t('common.save') }}
            </VcButton>
        </template>
    </RedesignBaseSideModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import cloneDeep from 'lodash/cloneDeep'
import { VcInput, VcInputNumber, VcButton, VcSwitch } from '@voicenter-team/voicenter-ui-plus'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'

/* Base modal */
import RedesignBaseSideModal
    from '@/components/RedesignComponents/Modal/RedesignBaseSideModal.vue'
import RedesignWidgetColor
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColor.vue'
import SoundSelect
    from '@/components/Common/SoundSelect.vue'
import { TIME_TYPE, PERCENTAGE_TYPE } from '@/enum/redesignQueueDashboardStatistics'

/* Data */
import { warningSoundList }
    from '@/composables/globalData/useEntitiesData'
const { t } = useI18n()

const props = defineProps<{
    visible: boolean
    metricKey: string
    type: string
    initialConfig: {
        customLabel: string
        customColor: string
        notificationConfiguration: {
            reverseThreshold: boolean
            warningThreshold: number
            limitThreshold: number
            warningColor: string
            limitColor: string
            warningSound: string
            limitSound: string
        }
    }
}>()

const emit = defineEmits<{
    (e: 'save', payload: {
        key: string
        config: typeof props.initialConfig
    }): void
    (e: 'close'): void
}>()

const localConfig = ref<typeof props.initialConfig | null>(null)
const { closeWithConfirm } = useConfirmBeforeClose()

function forceCloseModal () {
    emit('close')
}

function closeModal () {
    closeWithConfirm(forceCloseModal)
}

function onSave () {
    if (!localConfig.value) return

    emit('save', {
        key: props.metricKey,
        config: localConfig.value
    })

    emit('close')
}

function getLabelSuffix () {
    if (props.type === PERCENTAGE_TYPE) {
        return ' (in %)'
    }
    if (props.type === TIME_TYPE) {
        return ' (in sec)'
    }
    return ''
}

watch(
    () => [ props.visible, props.initialConfig ],
    ([ visible, config ]) => {
        if (visible && config) {
            localConfig.value = cloneDeep(config)
        }
    },
    { immediate: true }
)
</script>
